import {
	Sidebar,
	SidebarContent,
	SidebarFooter,
	SidebarHeader,
	SidebarInset,
	SidebarProvider,
	SidebarRail
} from '@repo/ui/sidebar'
import { Building2 } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from '@/lib/i18n'
import { useBrand } from '@/query/use-brand'
import { OperatorNavGroups } from './operator-nav-groups'
import { OperatorNavUser } from './operator-nav-user'

interface OperatorSidebarProps {
	children: ReactNode
}

function OperatorSidebar({ children }: OperatorSidebarProps) {
	const defaultOpen =
		typeof window !== 'undefined' &&
		window.matchMedia('(min-width: 1280px)').matches

	return (
		<SidebarProvider defaultOpen={defaultOpen}>
			<Sidebar collapsible='icon'>
				<SidebarHeader className='h-[65px] justify-center'>
					<OperatorBrand />
				</SidebarHeader>
				<SidebarContent>
					{/*
					 * Destinations only. Creating an organization is the
					 * organizations list's own act, on the trailing edge of its
					 * toolbar (`PageLayout`'s `actions`), not a rail entry that
					 * read as one more place to go.
					 */}
					<OperatorNavGroups />
				</SidebarContent>
				<SidebarFooter>
					<OperatorNavUser />
				</SidebarFooter>
				<SidebarRail />
			</Sidebar>
			<SidebarInset className='h-dvh min-w-0 overflow-hidden'>
				<div className='flex min-h-0 flex-1 flex-col'>{children}</div>
			</SidebarInset>
		</SidebarProvider>
	)
}

/**
 * The top slot of the rail: the instance's identity, drawn only from what it
 * published, and something neutral when it published nothing.
 *
 * Four drawings, two axes: shape by width (the lockup while the rail is open,
 * the mark once it collapses), ink by surface (`dark:`, which follows the
 * `.dark` class and not `prefers-color-scheme`). Only one ever displays; the
 * others are `display:none` and leave the accessibility tree, which is why
 * they carry the same alt text. There is no baked drawing to fall back to:
 *
 * - no logo, but a product name: the name as text;
 * - neither: the console's own generic word ("Backoffice"), which names what
 *   the screen is and claims no identity;
 * - no mark: the collapsed rail shows a neutral glyph instead of an `<img>`.
 *
 * With the product name unknown, a drawing is decorative (`alt=''`).
 */
function OperatorBrand() {
	const { t } = useTranslation()
	const { productName, logo, mark } = useBrand()
	const alt = productName
		? t('common:app_sidebar.brand_logo_alt', { product: productName })
		: ''

	return (
		<div className='flex items-center group-data-[collapsible=icon]:justify-center'>
			<div className='min-w-0 shrink-0 px-1 group-data-[collapsible=icon]:hidden'>
				{logo ? (
					<>
						{/*
						 * biome-ignore lint/correctness/useImageSize: a lockup's
						 * width belongs to the instance. Height is fixed at 24px and
						 * the width follows the artwork; declaring one would reserve
						 * the wrong box and cause the shift it is meant to prevent.
						 * `max-w-40` clips a very wide one.
						 */}
						<img
							alt={alt}
							className='h-6 w-auto max-w-40 dark:hidden'
							height={24}
							src={logo.light}
						/>
						{/* biome-ignore lint/correctness/useImageSize: as above. */}
						<img
							alt={alt}
							className='hidden h-6 w-auto max-w-40 dark:block'
							height={24}
							src={logo.dark}
						/>
					</>
				) : (
					<span className='block max-w-40 truncate font-semibold text-sm'>
						{productName ?? t('common:app_sidebar.console_name')}
					</span>
				)}
			</div>
			<div className='hidden shrink-0 group-data-[collapsible=icon]:block'>
				{mark ? (
					<>
						<img
							alt={alt}
							className='size-6 object-contain dark:hidden'
							height={24}
							src={mark.light}
							width={24}
						/>
						<img
							alt={alt}
							className='hidden size-6 object-contain dark:block'
							height={24}
							src={mark.dark}
							width={24}
						/>
					</>
				) : (
					<Building2
						aria-hidden='true'
						className='size-5 text-muted-foreground'
					/>
				)}
			</div>
		</div>
	)
}

export { OperatorSidebar }
